import React, { useEffect, useState } from "react";
import api from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";
import "./AdminMenuItems.css";

export default function AdminMenuItems() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    name: "",
    category: "Food",
    price: "",
    description: "",
    imageUrl: "",
    tags: "",
    popular: false,
    vegan: false,
    file: null,
    _id: null,
  });
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const fetchItems = async () => {
    try {
      // 🟢 Fetch all items (no pagination limit)
      const { data } = await api.get("/menu?all=true");
      let arr = [];
      if (Array.isArray(data)) arr = data;
      else if (Array.isArray(data.menuItems)) arr = data.menuItems;
      else if (Array.isArray(data.items)) arr = data.items;
      else arr = [];
      setItems(arr);
    } catch (err) {
      console.error(err);
      setItems([]);
    }
  };


  useEffect(() => {
    fetchItems();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Only images allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("Max 5MB");
      return;
    }
    setForm((prev) => ({ ...prev, file, imageUrl: URL.createObjectURL(file) }));
  };

  const resetForm = () =>
    setForm({
      name: "",
      category: "Food",
      price: "",
      description: "",
      imageUrl: "",
      tags: "",
      popular: false,
      vegan: false,
      file: null,
      _id: null,
    });

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.price) {
      alert("Name, category and price are required");
      return;
    }

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("category", form.category);
    fd.append("price", form.price);
    fd.append("description", form.description || "");
    fd.append("vegan", form.vegan ? "true" : "false");
    fd.append("popular", form.popular ? "true" : "false");

    if (form.file) {
      fd.append("image", form.file);
    } else if (form.imageUrl && !form.imageUrl.startsWith("blob:")) {
      fd.append("imageUrl", form.imageUrl);
    }

    try {
      setLoading(true);
      if (form._id) {
        await api.put(`/menu/${form._id}`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await api.post("/menu", fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      resetForm();
      fetchItems();
    } catch (err) {
      console.error("Save failed:", err.response?.data || err);
      alert("Save failed: " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (it) => {
    setForm({
      name: it.name,
      category: it.category,
      price: it.price,
      description: it.description,
      imageUrl: it.imageUrl || it.image || "",
      tags: it.tags?.join?.(", ") || "",
      file: null,
      popular: !!it.popular,
      vegan: !!it.vegan,
      _id: it._id,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete item?")) return;
    try {
      await api.delete(`/menu/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchItems();
    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  return (
    <div className="admin-menu">
      <header className="menu-header">
        <h1>Admin — Menu Items</h1>
        <button className="dashboard-btn" onClick={() => navigate("/admin/dashboard")}>Back to Dashboard</button>
      </header>

      <div className="menu-container">
        <div className="menu-form">
          <h3>{form._id ? "Edit Item" : "Add Item"}</h3>
          <form onSubmit={handleAddOrUpdate}>
            <div className="form-grid">
              <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                <option value="Food">Food</option>
                <option value="Set">Set</option>
                <option value="Dessert">Dessert</option>
                <option value="Drink">Drink</option>
              </select>
              <input placeholder="Price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
              <input placeholder="Image URL (optional)" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
              <input type="file" accept="image/*" onChange={handleFileChange} />
              {form.imageUrl && <img src={form.imageUrl} alt="preview" className="preview" />}
              <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <input placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
              <label><input type="checkbox" checked={form.popular} onChange={(e) => setForm({ ...form, popular: e.target.checked })} /> Popular</label>
              <label><input type="checkbox" checked={form.vegan} onChange={(e) => setForm({ ...form, vegan: e.target.checked })} />Vegan</label>

              <div className="form-buttons">
                <button type="submit" disabled={loading}>{loading ? "Saving..." : (form._id ? "Update" : "Add")}</button>
                {form._id && <button type="button" onClick={resetForm}>Cancel</button>}
              </div>
            </div>
          </form>
        </div>

        <div className="menu-list">
          <h3>Existing Items</h3>
          <div className="menu-items">
            {items.length === 0 && <div className="no-items">No items yet.</div>}
            {items.map((it) => (
              <div key={it._id} className="menu-item">
                <img src={it.imageUrl || it.image || "https://via.placeholder.com/80"} alt={it.name} />
                <div className="menu-info">
                  <div className="menu-name">{it.name}</div>
                  <div className="menu-meta">{it.category} • ${Number(it.price).toFixed(2)}{it.vegan && " • 🌱 Vegan"}</div>
                </div>
                <div className="menu-actions">
                  <button onClick={() => startEdit(it)}>Edit</button>
                  <button onClick={() => handleDelete(it._id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
